import SearchBar from 'components/Autosuggest/Autosuggest';
import Currency from 'components/Currency/Currency';
import DecimalInput from 'components/DecimalInput';
import MiniTag from 'components/MiniTag';
import WholeNumberInput from 'components/WholeNumberInput';
import React, { useState } from 'react';
import { searchItems } from 'services/api/inventoryItems';
import { showWarning } from 'utils';
import styles from './index.module.scss';

const AddInventoryItemModal = props => { 
  const [cost, setCost] = useState<number>(0);
  const [name, setName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [item, setItem] = useState<Record<string, any>>(props?.item || {});
  const [supplierUnit, setSupplierUnit] = useState<string>('');
  const [inventoryNameForSupplier, setInventoryNameForSupplier] = useState<string>('');
  const [itemSelected, setItemSelected] = useState<boolean>(props?.item ? true : false);
  const [selectedItems, setSelectedItems] = useState<Record<string, any>[]>([]);    // new selected items
  const [quantity, setQuantity] = useState<number>(props?.quantity || 1);

  const {
    relatedItems,
    toggleModal,
    heading,
    forRelatedItems = false,
    showQuantityInput = false,
    showUnitMappingInput = false,
    showInventoryNameForSupplierInput = false,
    onSubmit,
    disableSearch = false,
  } = props;

  const handleSuggestionClick = (suggestion: Record<string, any>) => {
    if (forRelatedItems) {
      const [itemAlreadySelected] = selectedItems.filter(item => item.id === suggestion.id);
      const [itemAlreadyAdded] = relatedItems.filter(item => item.objectId === suggestion.id);
      if (!itemAlreadyAdded && !itemAlreadySelected) {
        setSelectedItems([ ...selectedItems, suggestion ]);
      } else {
        showWarning('Item already added or selected');
      }
    } else {
      setItem(suggestion);
      setItemSelected(true);
    }
  };

  const renderNewSelectedItems = () => {
    if (!selectedItems.length) return null;

    const items: Record<string ,any>[] = selectedItems.map(item => ({
      objectId: item.id,
      name: item.displayName,
      removable: true,
      image: {
        _url: item.image || '',
      },
    }));
    return (
      <div className={styles['selected-items-container']}>
        <div className={styles['label']}>Selected Items</div>
        <div>
          {items.map(item => (
            <MiniTag
              key={item.objectId}
              loading={false}
              isRemovable={item.removable}
              isItemTag={true}
              image={item.image}
              label={item.name}
              onRemove={() => setSelectedItems(selectedItems.filter(selectedItem => selectedItem.id !== item.objectId))}
            />
          ))}
        </div>
      </div>
    );
  };
  
  const renderPreviouslyAddedItems = () => relatedItems?.length ? (
    <div className={styles['related-items-container']}>
      <div className={styles['label']}>Previously Added Items</div>
      <div>
        {relatedItems.map(item => (
          <MiniTag
            key={item.objectId}
            loading={false}
            isRemovable={false}
            isItemTag={true}
            image={item.image}
            label={item.name}
          />
        ))}
      </div>
    </div>
  ): null;
  
  const onClickSubmit = async(e) => {
    if(onSubmit){
      setLoading(true);
      await onSubmit({ item, quantity, supplierUnit, cost, selectedItems, inventoryNameForSupplier });
      setLoading(false);
    }
  };

  const ItemInfo = () => (
    <div className={styles['item-details']}>
      {item.image && (
        <div className={styles['item-image']}>
          <img src={item.image.url || item?.image} />
        </div>
      )}
      <div>
        <div className={styles['item-name-section']}>
          <div className={styles['label']}>Item Name</div>
          <div className={styles['item-name']}>{item.displayName || item.name || 'N/A'}</div>
        </div>
        <div className={styles['category-section']}>
          <div className={styles['label']}>Category</div>
          <div className={styles['item-category']}>{item.category?.displayName || 'N/A'}</div>
        </div>
        <div className={styles['price-section']}>
          <div className={styles['label']}>Price</div>
          <div className={styles['item-price']}>{<Currency amount={item.price}/>}</div>
        </div>
      </div>
    </div>
  );

  const getInventoryNameForSupplier = () => {
    return (
      <div className={'mb-3'}>
        <div className={styles['label']}>Inventory Name For Supplier</div>
        <input
          className={'form-control'}
          onChange={e => setInventoryNameForSupplier(e.target.value)}
        />
      </div>
    )
  }

  return (
    <div className={styles['main-container']}>
      <div className={styles['modal-container']}>
        <div className={styles['modal-header']}>
          <div>
            <div className={styles['label']}>Inventory Item</div>
            <div className={styles['modal-title']}>{heading}</div>
          </div>
          <i className={'fa fa-times'} onClick={toggleModal} />
        </div>
        <div className={styles['modal-body']}>
          <div className={'mb-3'}>
            <div className={styles['label']}>Item Name</div>
            <div className={styles['autosuggest-container']}>
              <SearchBar
                value={name}
                setValue={value => setName(value)}
                clearOnSelect={forRelatedItems ? true : false}
                searchAPI={searchItems}
                searchAPIParamKey={'term'}
                onSuggestionClicked={handleSuggestionClick}
                disabled={disableSearch}
              />
            </div>
          </div>
          {showInventoryNameForSupplierInput && getInventoryNameForSupplier()}
          {showUnitMappingInput && (
            <div>
              <div className={'mb-3'}>
                <div className={styles['label']}>Unit Mapping</div>
                <input
                  className={'form-control'}
                  onChange={e => setSupplierUnit(e.target.value)}
                />
              </div>
              <div>
                <div className={styles['label']}>Cost</div>
                <DecimalInput
                  value={cost}
                  onChange={setCost}
                />
              </div>
            </div>
          )}
          {(itemSelected && !forRelatedItems) && <ItemInfo />}
          <div className={styles['related-and-selected-items-container']}>
            {forRelatedItems && renderNewSelectedItems()}
            {forRelatedItems && renderPreviouslyAddedItems()}
          </div>
          {showQuantityInput && 
          <div>
            <div className={styles['label']}>Quantity</div>
            <WholeNumberInput className={'form-control'}
              value={quantity}
              onChange={setQuantity} 
              disabled={loading}
            />
          </div>
          }
        </div>
        <div className={styles['modal-footer']}>
          <button
            className={styles['cancel-button']}
            onClick={toggleModal}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={onClickSubmit}
            className={styles['submit-button']}
            disabled={loading}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddInventoryItemModal;
